import { inject, Injectable, OnDestroy } from '@angular/core';
import { Cart } from '../../models/cart';
import { BehaviorSubject, map, Observable, Subscription } from 'rxjs';
import { Product } from '../../models/product';
import { AuthService } from '../auth/auth.service';
import { Category } from '../../models/category';
import { CategoryService } from '../category/category.service';
import { ICartDataProvider } from '../../models/util/cartDataProvider';
import { LoggedCartProviderService } from './dataProviders/logged-cart-provider.service';
import { GuestCartProviderService } from './dataProviders/guest-cart-provider.service';
import _ from 'lodash';
import { isNoEmptyArray } from '@pineapplelab/util';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class CartService implements OnDestroy {

	cart: Cart = new Cart({ items: [], totalAmount: 0 });
	private categoriesSubject = new BehaviorSubject<Category[]>([]);
	private cartSubject = new BehaviorSubject<Cart>(this.cart);
	cart$ = this.cartSubject.asObservable();
	categories$ = this.categoriesSubject.asObservable();
	user = this.authService.auth.user;
	categoriesCount$: Observable<number> = this.categories$.pipe(map(categories => categories.length));

	private cartDataProviderSource: ICartDataProvider;
	private loggedStatusSubscription: Subscription;

	constructor(
		private authService: AuthService,
		private categoryService: CategoryService,
		private loggedCartProviderService: LoggedCartProviderService,
		private guestCartProviderService: GuestCartProviderService
	) {
		this.loggedStatusSubscription = this.authService.isLoggedStatus.subscribe(isLogged => {
			this.cartDataProviderSource = isLogged ? this.loggedCartProviderService : this.guestCartProviderService;
		});
	}

	ngOnDestroy(): void {
		if (this.loggedStatusSubscription) {
			this.loggedStatusSubscription.unsubscribe();
		}
	}

	async addToCart(product: Product): Promise<void> {
		const existingProduct = this.cart.items.find(item => item.id === product.id);
		if (!existingProduct) {
		this.cart.items.push(product);
		this.updateTotalAmount();
		await this.cartDataProviderSource.save(this.cart);
		await this.addCategory(product.id);
		}
		this.cartSubject.next(this.cart);
	}

	async removeFromCart(productId: string): Promise<void> {
		this.cart.items = this.cart.items.filter(item => item.id !== productId);
		this.updateTotalAmount();
		this.cartSubject.next(this.cart);

		this.removeCategory(productId);
		await this.cartDataProviderSource.save(this.cart);
	}

	async clearCart(): Promise<void> {
		await this.cartDataProviderSource.clear();

		this.cart.items = [];
		this.cart.totalAmount = 0;
		this.cartSubject.next(this.cart);
		this.categoriesSubject.next([]);
	}

	private updateTotalAmount(): void {
		this.cart.totalAmount = this.cart.items.reduce((sum, item) => sum + item.price, 0);
	}

	async loadCartAndCategories(projectId: string): Promise<void> {
		const cart = await this.cartDataProviderSource.get();
		if (cart) {
			this.cart = cart;
			this.cartSubject.next(this.cart);
		}

		if (this.cart.items.length > 0) {
		const categoriesPromises = this.cart.items.map(item =>
			this.categoryService.getById(item.id, projectId)
		);
		const categories = await Promise.all(categoriesPromises);
		this.categoriesSubject.next(categories);
		} else {
		this.categoriesSubject.next([]);
		}
	}

	private async addCategory(productId: string): Promise<void> {
		const category = await this.categoryService.getById(productId, this.authService.projectId);
		const currentCategories = this.categoriesSubject.value;
		if (!currentCategories.find(cat => cat.id === category.id)) {
		this.categoriesSubject.next([...currentCategories, category]);
		}
	}

	private removeCategory(productId: string): void {
		const currentCategories = this.categoriesSubject.value;
		const updatedCategories = currentCategories.filter(category => category.id !== productId);
		this.categoriesSubject.next(updatedCategories);
	}

	async migrateGuestCartToUser(userId: string): Promise<void> {
		if (!userId) {
			return;
		}

		const guestCart = this.guestCartProviderService.get();
		const loggedCart = await this.loggedCartProviderService.get();

		const productsToSave = new Set();

		if (isNoEmptyArray(loggedCart?.items)) {
			loggedCart.items.forEach(product => {
				this.handleCartMergingPush(product, productsToSave);
			});
		}
		if (isNoEmptyArray(guestCart?.items)) {
			guestCart.items.forEach(product => {
				this.handleCartMergingPush(product, productsToSave);
			});
		}

		guestCart.items = Array.from(productsToSave) as Product[];
		await this.loggedCartProviderService.save(guestCart, userId);
		this.guestCartProviderService.clear();
		await this.loadCartAndCategories(environment.projectId);
	}

	private handleCartMergingPush(item: Product, set: Set<any>): void {
		const uniqueArray = _.uniqBy([...set, item], 'id');
		set.clear();
		uniqueArray.forEach(item => set.add(item));
	}

	hasProductsToMerge(): boolean {
		const guestCart = this.guestCartProviderService.get();
		return guestCart?.items && isNoEmptyArray(guestCart?.items);
	}
}
